import { useParams, Link, Navigate } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, ChevronLeft, BookOpen } from 'lucide-react';
import { getPostBySlug, getRelatedPosts } from './posts';
import { SEO, orgJsonLd } from '../../components/SEO';

/** Render a mixed-content post (paragraphs, h2, lists, CTA). */
function PostBody({ blocks }) {
  return (
    <div className="prose max-w-none">
      {blocks.map((block, i) => {
        if (block.type === 'p') {
          return (
            <p key={i} className="text-[17px] text-[#0A0A0A] leading-[1.75] mb-5" dangerouslySetInnerHTML={{ __html: markdownInline(block.text) }} />
          );
        }
        if (block.type === 'h2') {
          return (
            <h2 key={i} className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mt-10 mb-4">
              {block.text}
            </h2>
          );
        }
        if (block.type === 'ul') {
          return (
            <ul key={i} className="space-y-2.5 mb-6 pl-0">
              {block.items.map((item, j) => (
                <li key={j} className="flex items-start gap-3 text-[17px] text-[#0A0A0A] leading-[1.7]">
                  <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#00A859] flex-shrink-0" />
                  <span dangerouslySetInnerHTML={{ __html: markdownInline(item) }} />
                </li>
              ))}
            </ul>
          );
        }
        if (block.type === 'cta') {
          return (
            <div key={i} className="mt-10 mb-6 rounded-2xl bg-[#11302A] p-8 md:p-10 text-center text-white">
              <p className="text-white/70 text-sm uppercase tracking-wider font-medium mb-3">Ready to try RaiseRoot?</p>
              <p className="font-heading text-2xl md:text-3xl font-bold mb-6">{block.text}</p>
              <Link to="/register">
                <button className="bg-[#00A859] text-white px-8 py-3.5 rounded-full font-medium hover:bg-[#008A4A] transition-all inline-flex items-center gap-2">
                  Start 7-Day Free Trial <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <p className="text-xs text-white/50 mt-3">Starter $19/mo · Growth $39/mo · Cancel anytime</p>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

// Very small inline markdown: **bold** → <strong>
function markdownInline(text) {
  return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  if (!post) return <Navigate to="/blog" replace />;
  const related = getRelatedPosts(slug, 2);

  const publishedISO = new Date(post.published).toISOString();

  return (
    <div className="min-h-screen bg-white" data-testid={`blog-post-${slug}`}>
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.description,
          datePublished: publishedISO,
          dateModified: publishedISO,
          author: { '@type': 'Organization', name: 'RaiseRoot' },
          publisher: orgJsonLd,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://raiseroot.co/blog/${post.slug}`,
          },
          keywords: post.keyword,
        }}
      />

      {/* Header */}
      <section className="pt-28 pb-10 md:pt-32 md:pb-12 bg-[#FAFAFA] border-b border-[#E2E8E6]">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B67] hover:text-[#00A859] mb-6" data-testid="back-to-blog">
            <ChevronLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          <div className="flex items-center gap-3 mb-4 text-xs text-[#5C6B67] flex-wrap">
            <span className="bg-[#00A859]/10 text-[#00A859] px-2.5 py-1 rounded-full font-medium">
              {post.category}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(post.published).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {post.readTime}
            </span>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] leading-[1.15]">
            {post.title}
          </h1>
        </div>
      </section>

      {/* Body */}
      <article className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <PostBody blocks={post.content} />
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="pb-20 border-t border-[#E2E8E6] pt-14 bg-[#FAFAFA]">
          <div className="max-w-3xl mx-auto px-6">
            <div className="flex items-center gap-2 text-[#00A859] text-sm font-medium tracking-wide uppercase mb-4">
              <BookOpen className="w-4 h-4" />
              Related Reading
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="block bg-white border border-[#E2E8E6] rounded-2xl p-5 hover:border-[#00A859]/40 transition-all"
                  data-testid={`related-post-${r.slug}`}
                >
                  <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mb-2">{r.title}</h3>
                  <p className="text-sm text-[#5C6B67] mb-3 line-clamp-2">{r.description}</p>
                  <span className="text-sm font-medium text-[#00A859] inline-flex items-center gap-1">
                    Read <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
